import React, {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
} from "react";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../business/plan/BusinessPlanModel";

// The combined state exposed by the Context
interface UserContextType {
  businessPlan: BusinessPlanModel;
  userInfo: BusinessStaticUserInfoWithUserUid;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  userUid: string;
  businessPlan: BusinessPlanModel;
  children: ReactNode;
}

const UserInit = ({ children }: PropsWithChildren) => {
  const { userInfo, businessPlan } = useUser();

  React.useEffect(() => {
    if (!userInfo.hasJoined) {
      void businessPlan.setAsJoined(userInfo.userUid);
    }
  }, [userInfo, businessPlan]);

  return children;
};

export const UserProvider: React.FC<UserProviderProps> = ({
  userUid,
  businessPlan,
  children,
}) => {
  const contextValue = React.useMemo<UserContextType>(
    () => ({
      businessPlan,
      userInfo: { userUid, ...businessPlan.data.userInfo[userUid] },
    }),
    [businessPlan, userUid],
  );

  return (
    <UserContext.Provider value={contextValue}>
      <UserInit>{children}</UserInit>
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
